import React, { useEffect, useRef } from 'react';
import './About.css';

const About: React.FC = () => {
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Auto-scroll effect (noticeable)
    let scrollTimer: NodeJS.Timeout | null = null;
    let currentScroll = 0;
    const scrollStep = 80; // pixels per step (higher for more noticeable movement)
    const scrollInterval = 40; // ms per step (lower for faster movement)

    const scrollDown = () => {
      if (scrollRef.current) {
        const scrollHeight = scrollRef.current.scrollHeight;
        const clientHeight = scrollRef.current.clientHeight;
        const maxScroll = scrollHeight - clientHeight;

        scrollTimer = setInterval(() => {
          if (!scrollRef.current) return;
          currentScroll = scrollRef.current.scrollTop + scrollStep;
          if (currentScroll >= maxScroll) {
            // Reset to top when reaching bottom
            currentScroll = 0;
          }
          scrollRef.current.scrollTo({
            top: currentScroll,
            behavior: 'smooth'
          });
        }, scrollInterval);
      }
    };

    // Start auto-scroll after a short delay
    const startDelay = setTimeout(scrollDown, 1000);

    return () => {
      clearTimeout(startDelay);
      if (scrollTimer) clearInterval(scrollTimer);
    };
  }, []);

  return (
    <div className="about-page" ref={scrollRef}>
      {/* Hero Section */}
      <section className="about-hero">
        <div className="container">
          <div className="about-hero-content">
            <h1 className="about-hero-title">
              About <span className="highlight">Me</span>
            </h1>
            <p className="about-hero-description">
              Get to know me better - my journey, skills, and what drives me in the world of technology.
            </p>
          </div>
        </div>
      </section>

      {/* Main Content Section */}
      <section className="about-content">
        <div className="container">
          <div className="about-grid">
            {/* Profile Image with Triangular Decorations */}
            <div className="about-image-section">
              <div className="image-container">
                <div className="triangular-decoration top-left"></div>
                <div className="triangular-decoration top-right"></div>
                <div className="triangular-decoration bottom-left"></div>
                <div className="triangular-decoration bottom-right"></div>
                <div className="image-frame">
                  <img 
                    src="/nirajan.png" 
                    alt="Nirajan Bhattarai" 
                    className="profile-image"
                  />
                </div>
                <div className="floating-elements">
                  <div className="floating-dot dot-1"></div>
                  <div className="floating-dot dot-2"></div>
                  <div className="floating-dot dot-3"></div>
                  <div className="floating-line line-1"></div>
                  <div className="floating-line line-2"></div>
                </div>
              </div>
            </div>

            {/* Personal Information */}
            <div className="about-info-section">
              <div className="info-card">
                <h2 className="info-title">Personal Information</h2>
                <div className="info-grid">
                  <div className="info-item">
                    <span className="info-label">Full Name:</span>
                    <span className="info-value">Nirajan Bhattarai</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Location:</span>
                    <span className="info-value">Kathmandu, Nepal</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Email:</span>
                    <span className="info-value">nirajanbhattarai12345@gmail.com</span>
                  </div>
                  
                </div>
              </div>

              <div className="info-card">
                <h2 className="info-title">Professional Summary</h2>
                <p className="summary-text">
                  I am a passionate and innovative software developer with expertise in full-stack development, 
                  mobile applications, and modern web technologies. With a strong foundation in both frontend and 
                  backend development, I specialize in creating scalable, user-friendly applications that solve 
                  real-world problems.
                </p>
                <p className="summary-text">
                  My journey in technology has been driven by curiosity and a desire to build solutions that 
                  make a difference. I have successfully led teams in hackathons, winning multiple competitions 
                  and demonstrating my ability to think creatively and execute effectively under pressure.
                </p>
              </div>

              <div className="info-card">
                <h2 className="info-title">Education</h2>
                <div className="education-item">
                  <div className="education-header">
                    <h3 className="degree">Bachelor's Degree in Computer Science & Information Technology</h3>
                    <span className="institution">[Tribhuvan University]</span>
                  </div>
                  <div className="education-details">
                    <span className="year">2020 - 2024</span>
                    <span className="location">Kathmandu, Nepal</span>
                  </div>
                  <p className="education-description">
                    Studied computer science fundamentals, algorithms, data structures, and software engineering principles.
                  </p>
                </div>
              </div>

              {/* Career Highlights Roadmap */}
              <div className="info-card">
                <h2 className="info-title">Career Highlights</h2>
                <div className="career-roadmap">
                  <div className="roadmap-line"></div>
                  
                  {/* Career Stop 1 */}
                  <div className="roadmap-stop stop-1">
                    <div className="stop-marker">
                      <div className="marker-dot"></div>
                      <div className="marker-pulse"></div>
                    </div>
                    <div className="stop-content">
                      <div className="stop-year">2023 - 2025</div>
                      <h4 className="stop-title">Kabim Technologies</h4>
                      <p className="stop-description">Full Stack Developer</p>
                      <div className="stop-details">
                        <span className="stop-role">Full Stack Developer</span>
                        <span className="stop-location">Kathmandu, Nepal</span>
                      </div>
                    </div>
                  </div>

                  {/* Career Stop 2 */}
                  <div className="roadmap-stop stop-2">
                    <div className="stop-marker">
                      <div className="marker-dot"></div>
                      <div className="marker-pulse"></div>
                    </div>
                    <div className="stop-content">
                      <div className="stop-year">2024</div>
                      <h4 className="stop-title">Hackathon Victories</h4>
                      <p className="stop-description">Multiple Competition Wins</p>
                      <div className="stop-details">
                        <span className="stop-role">🏆 Winner</span>
                        <span className="stop-location">National Level</span>
                      </div>
                    </div>
                  </div>

                  {/* Career Stop 3 */}
                  <div className="roadmap-stop stop-3">
                    <div className="stop-marker">
                      <div className="marker-dot"></div>
                      <div className="marker-pulse"></div>
                    </div>
                    <div className="stop-content">
                      <div className="stop-year">2022 - 2023</div>
                      <h4 className="stop-title">Vision Tech</h4>
                      <p className="stop-description">Internship Experience</p>
                      <div className="stop-details">
                        <span className="stop-role">Intern</span>
                        <span className="stop-location">Kathmandu, Nepal</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="info-card">
                <h2 className="info-title">Interests & Hobbies</h2>
                <div className="interests-grid">
                  <span className="interest-tag">Technology</span>
                  <span className="interest-tag">Problem Solving</span>
                  <span className="interest-tag">Innovation</span>
                  <span className="interest-tag">Learning</span>
                  <span className="interest-tag">Team Collaboration</span>
                  <span className="interest-tag">Open Source</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
